// ===================================================
// ФАЙЛ: frontend/src/contexts/OnboardingContext.jsx
// Единый контур онбординга — прогресс первых 30 дней
// MASTER_PLAN §9.2A–C
// ===================================================
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import api from '../utils/axios';
import { pickSafeRussianBackendMessage, pickHttpStatusRussianFallback } from '../utils/safeRussianBackendMessage';

const OnboardingContext = createContext(null);

// Канонический путь до первого живого товара (id синхронизированы с backend/onboarding.js STEP_DEFS).
// path — куда ведёт шаг, cta — короткая подпись действия, openWizard — открыть мастер компании.
export const ONBOARDING_STEPS = [
  {
    id: 'company_setup',
    label: 'Настройка компании',
    desc: 'Реквизиты, бренд, НДС и единицы измерения',
    path: '/our-company?tab=requisites',
    cta: 'Реквизиты компании',
    openWizard: false,
    required: true,
  },
  {
    id: 'agreements_signed',
    label: 'Соглашения компании',
    desc: 'Подпишите обязательные соглашения',
    path: '/settings?tab=consents',
    cta: 'Открыть соглашения',
    required: true,
  },
  {
    id: 'warehouse_setup',
    label: 'Настройка склада',
    desc: 'Добавьте склад для учёта остатков',
    path: '/warehouses',
    cta: 'Настроить склад',
    required: false,
  },
  {
    id: 'supplier_setup',
    label: 'Добавьте поставщика',
    desc: 'Подключите поставщика или импорт каталога',
    path: '/suppliers',
    cta: 'Добавить поставщика',
    required: false,
  },
  {
    id: 'products_added',
    label: 'Добавьте первые товары',
    desc: 'Создайте или импортируйте товары',
    path: '/products',
    cta: 'Добавить товар',
    required: true,
  },
  {
    id: 'storefront_live',
    label: 'Витрина и продажи',
    desc: 'Синхронизируйте каталог в магазин — товар станет «живым»',
    path: '/commerce-hub',
    cta: 'Проверить витрину',
    required: true,
  },
  {
    id: 'channel_telegram',
    label: 'Telegram-продажи',
    desc: 'Подключите Telegram-бот магазина',
    path: '/commerce-hub',
    cta: 'Настроить Telegram',
    required: false,
  },
];

const LS_KEY = 'mb_onboarding_state';
const ONBOARDING_WINDOW_DAYS = 30;

function loadLocalState() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveLocalState(state) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
}

export function OnboardingProvider({ children }) {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const company = useSelector((s) => s.auth.company);
  const contextReady = useSelector((s) => s.auth.isAuthenticated && s.auth.bootstrapStatus === 'ready');

  const [completedSteps, setCompletedSteps] = useState([]);
  const [isWithinWindow, setIsWithinWindow] = useState(false);
  const [requiredComplete, setRequiredComplete] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [masterProgress, setMasterProgress] = useState(null);
  const [masterNextAction, setMasterNextAction] = useState(null);
  const syncPending = useRef(false);
  /** Одно предупреждение на логический отказ GET /api/onboarding/status (200 + success:false) */
  const onboardingStatusLogicalWarnedRef = useRef(false);

  const userId = contextReady ? user?.id : null;
  const companyId = contextReady ? company?.id : null;
  const contextScopeRef = useRef(null);
  contextScopeRef.current = userId && companyId ? String(userId) + ':' + String(companyId) : null;

  useEffect(() => {
    onboardingStatusLogicalWarnedRef.current = false;
  }, [userId, companyId]);

  // Прогресс: приоритет — единый мастер /commerce-hub (Ф3), иначе legacy onboarding steps.
  const legacyProgress = Math.round((completedSteps.length / ONBOARDING_STEPS.length) * 100);
  const progress = masterProgress != null ? masterProgress : legacyProgress;
  const isComplete = masterProgress === 100 || completedSteps.length >= ONBOARDING_STEPS.length;
  // Мастер не блокирует работу, как только выполнены обязательные шаги; но продолжает вести
  // по необязательным (витрина, Telegram) до 100%.
  const isOnboarding = isWithinWindow && !isComplete;

  const applyStatusBody = useCallback((body) => {
    if (!body?.success) return false;
    const data = body.data || {};
    const serverSteps = Array.isArray(data.steps) ? data.steps : null;
    const completedIds = serverSteps
      ? serverSteps.filter((s) => s.completed).map((s) => s.id)
      : (data.completed_steps || []);
    const withinWindow = isWithinOnboardingWindow(data.created_at);
    setCompletedSteps(completedIds);
    setRequiredComplete(data.completed === true);
    setIsWithinWindow(withinWindow);
    saveLocalState({ userId, companyId, completedSteps: completedIds, isWithinWindow: withinWindow });
    return true;
  }, [userId, companyId]);

  const applyMasterProgress = useCallback((hubBody) => {
    if (!hubBody) return;
    const sp = hubBody.setup_progress;
    if (sp) {
      if (typeof sp.percent === 'number') {
        setMasterProgress(sp.percent);
      } else if (sp.total > 0) {
        setMasterProgress(Math.round((sp.ok / sp.total) * 100));
      }
    } else if (typeof hubBody.overall_percent === 'number') {
      setMasterProgress(hubBody.overall_percent);
    }
    if (hubBody.next_action && typeof hubBody.next_action === 'object') {
      setMasterNextAction({
        label: hubBody.next_action.label || hubBody.next_action.title || null,
        path: hubBody.next_action.path || null,
        probe_id: hubBody.next_action.probe_id || null,
        scene_id: hubBody.next_action.scene_id || null,
        external: hubBody.next_action.external === true
          || /^https?:\/\//i.test(String(hubBody.next_action.path || '')),
        openWizard: hubBody.next_action.openWizard === true
          || hubBody.next_action.secondary_action === 'open_company_wizard',
      });
    } else if (hubBody.next_action === null) {
      setMasterNextAction(null);
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    const requestScope = contextScopeRef.current;
    if (!requestScope) return;
    try {
      const [onbRes, hubRes] = await Promise.all([
        api.get('/api/onboarding/status', { errorPresentation: 'inline', noDedup: true, cacheTtlMs: 0 }),
        api.get('/api/commerce-hub/status', { errorPresentation: 'inline', noDedup: true, cacheTtlMs: 0 }).catch(() => null),
      ]);
      if (contextScopeRef.current !== requestScope) return;
      applyStatusBody(onbRes?.data);
      if (hubRes?.data) applyMasterProgress(hubRes.data);
    } catch {
      /* axios interceptor уже покажет ошибку при необходимости */
    }
  }, [userId, companyId, applyStatusBody, applyMasterProgress]);

  // Загрузка состояния с сервера (при наличии пользователя)
  useEffect(() => {
    if (!userId || !companyId) {
      setCompletedSteps([]);
      setIsWithinWindow(false);
      setRequiredComplete(false);
      setMasterProgress(null);
      setMasterNextAction(null);
      setLoaded(true);
      return;
    }

    let cancelled = false;

    const loadStatus = async () => {
      const local = loadLocalState();

      try {
        const [onbRes, hubRes] = await Promise.all([
          api.get('/api/onboarding/status', { errorPresentation: 'inline', noDedup: true, cacheTtlMs: 0 }),
          api.get('/api/commerce-hub/status', { errorPresentation: 'inline', noDedup: true, cacheTtlMs: 0 }).catch(() => null),
        ]);
        if (cancelled) return;
        const body = onbRes?.data;
        if (body?.success) {
          onboardingStatusLogicalWarnedRef.current = false;
          applyStatusBody(body);
        } else if (body && body.success === false && !onboardingStatusLogicalWarnedRef.current) {
          onboardingStatusLogicalWarnedRef.current = true;
          message.warning(
            pickSafeRussianBackendMessage(
              body.error ?? body.message,
              pickHttpStatusRussianFallback(onbRes.status)
            )
          );
        }
        if (hubRes?.data) applyMasterProgress(hubRes.data);
      } catch {
        if (!cancelled && local && local.userId === userId && local.companyId === companyId) {
          setCompletedSteps(local.completedSteps || []);
          setIsWithinWindow(local.isWithinWindow ?? false);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    loadStatus();
    return () => { cancelled = true; };
  }, [userId, companyId, applyStatusBody, applyMasterProgress]);

  // Companion / Hub refresh → sync masterNextAction (fixes HelperSidebar stale «Открыть соглашения»).
  useEffect(() => {
    const onHealth = (e) => {
      if (!contextScopeRef.current) return;
      const d = e?.detail;
      if (!d) return;
      applyMasterProgress({
        next_action: d.next_action,
        setup_progress: d.setup_progress,
        overall_percent: d.overall_percent,
      });
    };
    const onHub = (e) => {
      if (!contextScopeRef.current) return;
      const d = e?.detail;
      if (!d) return;
      applyMasterProgress(d);
    };
    window.addEventListener('waysen_setup_health_updated', onHealth);
    window.addEventListener('commerce_hub_status_updated', onHub);
    return () => {
      window.removeEventListener('waysen_setup_health_updated', onHealth);
      window.removeEventListener('commerce_hub_status_updated', onHub);
    };
  }, [applyMasterProgress]);

  // Пометить шаг завершённым
  const completeStep = useCallback(async (stepId) => {
    if (!userId || !companyId) return;
    if (completedSteps.includes(stepId) || syncPending.current) return;

    const requestScope = contextScopeRef.current;
    syncPending.current = true;
    try {
      const res = await api.post(`/api/onboarding/step/${stepId}/complete`);
      if (contextScopeRef.current !== requestScope) return;
      if (res?.data?.success) {
        const next = [...completedSteps, stepId];
        setCompletedSteps(next);
        saveLocalState({ userId, companyId, completedSteps: next, isWithinWindow });
      } else {
        const b = res?.data || {};
        message.warning(
          pickSafeRussianBackendMessage(
            b.error ?? b.message,
            pickHttpStatusRussianFallback(res.status)
          )
        );
      }
    } catch {
      // Отклонение axios: toast обычно уже в `utils/axios.js`
    } finally {
      syncPending.current = false;
    }
  }, [completedSteps, userId, companyId, isWithinWindow]);

  /** Opens WaySen Companion + Commerce Hub (replaces deleted TutorialOverlay). */
  const startTutorial = useCallback(() => {
    window.dispatchEvent(new CustomEvent('open_waysen_companion', {
      detail: { mode: 'guide', asWindow: false },
    }));
    navigate('/commerce-hub');
  }, [navigate]);

  // Глобальный триггер открытия единого Мастера настройки (из сайдбара, после создания компании и т.д.).
  useEffect(() => {
    const handler = () => {
      refreshStatus();
      navigate('/commerce-hub');
      // After first company create the FAQ promises WaySen will guide — open Guide, not Hub alone.
      window.setTimeout(() => {
        try {
          window.dispatchEvent(new CustomEvent('open_waysen_companion', {
            detail: { mode: 'guide', asWindow: true },
          }));
        } catch (_) { /* noop */ }
      }, 350);
    };
    window.addEventListener('open_setup_master', handler);
    return () => window.removeEventListener('open_setup_master', handler);
  }, [navigate, refreshStatus]);

  const value = {
    isOnboarding,
    isComplete,
    requiredComplete,
    isWithinWindow,
    progress,
    completedSteps,
    currentStep: null,
    steps: ONBOARDING_STEPS,
    masterNextAction,
    completeStep,
    showStep: () => {},
    dismissStep: () => {},
    startTutorial,
    dismissTutorial: () => {},
    refreshStatus,
    loaded,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    // Graceful fallback — компонент может рендериться вне провайдера
    return {
      isOnboarding: false,
      isComplete: true,
      requiredComplete: true,
      isWithinWindow: false,
      progress: 100,
      completedSteps: [],
      currentStep: null,
      steps: ONBOARDING_STEPS,
      masterNextAction: null,
      completeStep: () => {},
      showStep: () => {},
      dismissStep: () => {},
      startTutorial: () => {},
      dismissTutorial: () => {},
      refreshStatus: () => {},
      loaded: true,
    };
  }
  return ctx;
}

function isWithinOnboardingWindow(createdAt) {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = (now - created) / (1000 * 60 * 60 * 24);
  return diffDays <= ONBOARDING_WINDOW_DAYS;
}

export default OnboardingContext;
